import {
  to12HourTime,
  to24HourTime,
} from "@/components/news-alerts/shared/utils";
import toast from "@/components/ui/toast";
import { createHeaders } from "@/data-access/core/authenticatedFetch";
import { dataServiceBaseUrl } from "@/data-access/core/constants";
import {
  CreateNewsAlertMutation,
  DeleteNewsAlertMutation,
  GenerateNewsAlertPreviewMutation,
  NewsAlertsQuery,
  UpdateNewsAlertMutation,
  UploadNewsAlertLogoMutationString,
} from "@/data-access/news-alerts/queries/news-alerts-queries";
import type { TimezoneChoices } from "@/data-access/news-alerts/queries/timezone-queries";
import { client } from "@/lib/urqlProvider";
import useAuth from "@/store/auth.slice";
import {
  closeCreateNewsAlertModal,
  resetCreateEditNewsAlert,
  resetCreateEditNewsAlertLogo,
  setIsCreatingNewsAlert,
  setIsEditingNewsAlert,
  setIsFetchingNewsAlertById,
  setIsGeneratingNewsAlertPreview,
  setIsUploadingNewsAlertLogo,
  setNewsAlertLogoId,
  setNewsAlertLogoPreviewUrl,
  updateCreateEditNewsAlert,
} from "./news-alerts.actions";
import useNewsAlertsStore, {
  type AlertCreationAndEditState,
} from "./news-alerts.slice";

const createNewsAlert = async () => {
  const toastMessages = {
    error: {
      failed: {
        type: "error" as const,
        title: "Oops! Something went wrong",
        description: "We couldn't create the news alert right now.",
      },
    },
    success: {
      type: "success" as const,
      title: "Success",
      description: "News alert created successfully",
    },
  };

  try {
    setIsCreatingNewsAlert(true);

    const { createEditNewsAlert } = useNewsAlertsStore.getState();

    const formattedScheduleTime = to24HourTime(
      createEditNewsAlert.scheduleTime,
    );

    const result = await client.mutation(CreateNewsAlertMutation, {
      input: {
        sections: Array.from(createEditNewsAlert.sections.values()).map(
          (section) => ({
            name: section.name,
            feedId: section.feedId ?? "",
            savedViewId: section.savedViewId ?? "",
            articleLimit: section.articleLimit,
            sortOrder: section.sortOrder,
          }),
        ),
        newsAlert: {
          name: createEditNewsAlert.alertName,
          description: createEditNewsAlert.description,
          recipients: Array.from(createEditNewsAlert.recipients),
          sendOn: createEditNewsAlert.scheduleFrequency,
          sendAt: formattedScheduleTime,
          timezone: createEditNewsAlert.timezone as TimezoneChoices,
          logoId: createEditNewsAlert.logoId ?? null,
        },
      },
    });

    if (result.error) {
      toast.error(toastMessages.error.failed.description);
      setIsCreatingNewsAlert(false);
      return false;
    }

    if (result.data?.createNewsAlert?.__typename === "OperationInfo") {
      const errorMsg = result.data?.createNewsAlert?.messages?.[0]?.message;
      toast.error(errorMsg);
      setIsCreatingNewsAlert(false);
      return false;
    }

    if (result.data?.createNewsAlert?.__typename !== "NewsAlertType") {
      toast.error("An error occurred while creating the news alert.");
      setIsCreatingNewsAlert(false);
      return false;
    }

    setIsCreatingNewsAlert(false);
    closeCreateNewsAlertModal();
    resetCreateEditNewsAlert();
    toast.success(toastMessages.success.description);

    return true;
  } catch (error) {
    toast.error("An error occurred while creating the news alert.");
    setIsCreatingNewsAlert(false);
    return false;
  }
};

const fetchNewsAlertById = async (id: string) => {
  const toastMessages = {
    error: {
      failed: {
        type: "error" as const,
        title: "Oops! Something went wrong",
        description: "We couldn't fetch the news alerts right now.",
      },
    },
    noAlerts: {
      type: "error" as const,
      title: "No news alerts found",
      description: "We couldn't find any news alerts right now.",
    },
  };

  if (!id) {
    return false;
  }

  // Set the editing ID at the start
  useNewsAlertsStore.setState({ editNewsAlertId: id });

  try {
    setIsFetchingNewsAlertById(true);

    const result = await client.query(NewsAlertsQuery, {
      filters: {
        id: id,
      },
    });

    if (result.error) {
      toast.error(toastMessages.error.failed.description);
      setIsFetchingNewsAlertById(false);
      // Clear the edit ID on error
      useNewsAlertsStore.setState({ editNewsAlertId: null });
      return false;
    }

    if (!result.data?.newsAlerts) {
      toast.error(toastMessages.noAlerts.description);
      setIsFetchingNewsAlertById(false);
      // Clear the edit ID on error
      useNewsAlertsStore.setState({ editNewsAlertId: null });
      return false;
    }

    if (!result.data.newsAlerts[0]) {
      toast.error(toastMessages.noAlerts.description);
      setIsFetchingNewsAlertById(false);
      // Clear the edit ID on error
      useNewsAlertsStore.setState({ editNewsAlertId: null });
      return false;
    }

    const alert = result.data.newsAlerts[0];

    const newsAlert: AlertCreationAndEditState = {
      id: alert.id,
      alertName: alert.name,
      description: alert.description,
      scheduleFrequency: alert.sendOn,
      scheduleTime: to12HourTime(
        typeof alert.sendAt === "string" ? alert.sendAt : "08:00:00",
      ),
      timezone: alert.timezone,
      recipients: new Set(alert.recipients),
      sections: new Map(
        alert.sections.map((section) => [
          section.id,
          {
            id: section.id,
            name: section.name ?? "",
            feedId: section.feed?.id ?? null,
            savedViewId: section.savedView?.id ?? null,
            articleLimit: section.articleLimit,
            sortOrder: section.sortOrder,
          },
        ]),
      ),
      logoFile: alert.alertLogo?.logo as File | null,
      logoPreviewUrl: alert.alertLogo?.logoUrl ?? null,
      logoId: alert.alertLogo?.id ?? null,
    };

    updateCreateEditNewsAlert(newsAlert);
    setIsFetchingNewsAlertById(false);

    return true;
  } catch (error) {
    toast.error("An error occurred while fetching the news alert by ID.");
    setIsFetchingNewsAlertById(false);
    // Clear the edit ID on error
    useNewsAlertsStore.setState({ editNewsAlertId: null });
    return false;
  }
};

/**
 * Delete a news alert
 * Gets the alert ID and name from store state
 * @returns Success status and optional error messages
 */
const deleteNewsAlert = async () => {
  const { deleteNewsAlertId, createEditNewsAlert } =
    useNewsAlertsStore.getState();
  const alertName = createEditNewsAlert.alertName;

  if (!deleteNewsAlertId) {
    toast({
      type: "error",
      title: "Oops! Something went wrong",
      description: "We couldn't find the news alert.",
    });

    return {
      success: false,
      errors: ["News alert ID is required"],
    };
  }

  // Set loading state
  useNewsAlertsStore.setState({ isDeletingNewsAlert: true });

  try {
    const result = await client.mutation(DeleteNewsAlertMutation, {
      newsAlertId: deleteNewsAlertId,
    });

    // Handle OperationInfo errors
    if (result.data?.deleteNewsAlert?.__typename === "OperationInfo") {
      const messages =
        result.data?.deleteNewsAlert?.messages?.map((m) => m.message) ?? [];

      toast({
        type: "error",
        title: "Oops! Something went wrong",
        description:
          messages[0] || "We couldn't delete the news alert right now.",
      });

      useNewsAlertsStore.setState({ isDeletingNewsAlert: false });

      return {
        success: false,
        errors: messages,
      };
    }

    // Handle SuccessResponse
    if (result.data?.deleteNewsAlert?.__typename === "SuccessResponse") {
      useNewsAlertsStore.setState({
        isDeletingNewsAlert: false,
        isDeleteNewsAlertModalOpen: false,
        deleteNewsAlertId: null,
      });

      toast({
        type: "success",
        title: "News alert deleted!",
        description: `"${alertName}" has been deleted successfully.`,
      });

      return {
        success: true,
        message: result.data.deleteNewsAlert.message,
      };
    }

    // Handle unexpected errors
    if (result.error) {
      toast({
        type: "error",
        title: "Oops! Something went wrong",
        description: "We couldn't delete the news alert right now.",
      });

      useNewsAlertsStore.setState({ isDeletingNewsAlert: false });

      return {
        success: false,
        errors: [result.error.message],
      };
    }

    toast({
      type: "error",
      title: "Oops! Something went wrong",
      description: "We couldn't delete the news alert right now.",
    });

    useNewsAlertsStore.setState({ isDeletingNewsAlert: false });

    return {
      success: false,
      errors: ["Unknown error occurred"],
    };
  } catch (error) {
    toast({
      type: "error",
      title: "Oops! Something went wrong",
      description: "We couldn't delete the news alert right now.",
    });

    useNewsAlertsStore.setState({ isDeletingNewsAlert: false });

    return {
      success: false,
      errors: [error instanceof Error ? error.message : "Unknown error"],
    };
  }
};

const generateNewsAlertPreview = async () => {
  const toastMessages = {
    error: {
      failed: {
        type: "error" as const,
        title: "Oops! Something went wrong",
        description:
          "Preview generation failed. Please check your alert configuration and try again.",
      },
      invalidURL: {
        type: "error" as const,
        title: "Oops! Something went wrong",
        description: "Invalid preview URL received",
      },
    },
  };
  setIsGeneratingNewsAlertPreview(true);

  const { createEditNewsAlert } = useNewsAlertsStore.getState();

  const formattedScheduleTime = to24HourTime(createEditNewsAlert.scheduleTime);

  try {
    const result = await client.mutation(GenerateNewsAlertPreviewMutation, {
      input: {
        sections: Array.from(createEditNewsAlert.sections.values()).map(
          (section) => ({
            name: section.name,
            feedId: section.feedId ?? "",
            savedViewId: section.savedViewId ?? "",
            articleLimit: section.articleLimit,
            sortOrder: section.sortOrder,
          }),
        ),
        newsAlert: {
          name: createEditNewsAlert.alertName,
          description: createEditNewsAlert.description,
          recipients: Array.from(createEditNewsAlert.recipients),
          sendOn: createEditNewsAlert.scheduleFrequency,
          sendAt: formattedScheduleTime,
          timezone: createEditNewsAlert.timezone || "LOCALTIME",
          logoId: createEditNewsAlert.logoId ?? null,
        },
      },
    });

    if (result.error) {
      toast.error(toastMessages.error.failed.description);
      setIsGeneratingNewsAlertPreview(false);
      return false;
    }

    if (
      result.data?.generateNewsAlertPdfPreview?.__typename === "OperationInfo"
    ) {
      const errorMsg =
        result.data?.generateNewsAlertPdfPreview?.messages?.[0]?.message;
      toast.error(errorMsg);
      setIsGeneratingNewsAlertPreview(false);
      return false;
    }

    if (
      result.data?.generateNewsAlertPdfPreview?.__typename !==
      "NewsAlertPDFPreviewResponse"
    ) {
      toast.error(toastMessages.error.failed.description);
      setIsGeneratingNewsAlertPreview(false);
      return false;
    }

    const previewUrl = result.data?.generateNewsAlertPdfPreview?.url;
    if (!previewUrl) {
      toast.error(toastMessages.error.failed.description);
      setIsGeneratingNewsAlertPreview(false);
      return false;
    }

    if (!previewUrl.startsWith("https://") || !URL.canParse(previewUrl)) {
      toast.error(toastMessages.error.invalidURL.description);
      setIsGeneratingNewsAlertPreview(false);
      return false;
    }

    window.open(previewUrl, "_blank");
    setIsGeneratingNewsAlertPreview(false);
    return true;
  } catch (error) {
    toast.error("An error occurred while generating the news alert preview.");
    setIsGeneratingNewsAlertPreview(false);
    return false;
  }
};

const updateNewsAlert = async () => {
  const toastMessages = {
    error: {
      failed: {
        type: "error" as const,
        title: "Oops! Something went wrong",
        description: "We couldn't update the news alert right now.",
      },
    },
    success: {
      type: "success" as const,
      title: "Success",
      description: "News alert updated successfully",
    },
  };

  const { createEditNewsAlert } = useNewsAlertsStore.getState();

  const formattedScheduleTime = to24HourTime(createEditNewsAlert.scheduleTime);

  try {
    setIsEditingNewsAlert(true);
    const result = await client.mutation(UpdateNewsAlertMutation, {
      input: {
        newsAlertId: createEditNewsAlert.id || "",
        sections: Array.from(createEditNewsAlert.sections.values()).map(
          (section) => ({
            ...(section.id && { id: section.id }),
            name: section.name,
            savedViewId: section.savedViewId ?? "",
            feedId: section.feedId ?? "",
            articleLimit: section.articleLimit,
            sortOrder: section.sortOrder,
          }),
        ),
        newsAlert: {
          name: createEditNewsAlert.alertName,
          description: createEditNewsAlert.description,
          recipients: Array.from(createEditNewsAlert.recipients),
          sendOn: createEditNewsAlert.scheduleFrequency,
          sendAt: formattedScheduleTime,
          timezone: createEditNewsAlert.timezone as TimezoneChoices,
          logoId: createEditNewsAlert.logoId ?? null,
        },
      },
    });

    if (result.error) {
      toast.error("An error occurred while updating the news alert.");
      setIsEditingNewsAlert(false);
      // Don't clear editNewsAlertId on error - keep user in edit mode so they can retry
      return false;
    }

    if (result.data?.updateNewsAlert?.__typename === "OperationInfo") {
      const errorMsg = result.data?.updateNewsAlert?.messages?.[0]?.message;
      toast.error(errorMsg);
      setIsEditingNewsAlert(false);
      // Don't clear editNewsAlertId on error - keep user in edit mode so they can retry
      return false;
    }

    if (result.data?.updateNewsAlert?.__typename !== "NewsAlertType") {
      toast.error("An error occurred while updating the news alert.");
      setIsEditingNewsAlert(false);
      // Don't clear editNewsAlertId on error - keep user in edit mode so they can retry
      return false;
    }

    setIsEditingNewsAlert(false);
    useNewsAlertsStore.setState({ editNewsAlertId: null });
    closeCreateNewsAlertModal();
    resetCreateEditNewsAlert();
    toast.success(toastMessages.success.description);

    return true;
  } catch (error) {
    toast.error("An error occurred while updating the news alert.");
    setIsEditingNewsAlert(false);
    // Don't clear editNewsAlertId on error - keep user in edit mode so they can retry
    return false;
  }
};

const uploadNewsAlertLogo = async () => {
  const toastMessages = {
    error: {
      failed: {
        type: "error" as const,
        title: "Oops! Something went wrong",
        description: "We couldn't upload the news alert logo right now.",
      },
      noFile: {
        type: "error" as const,
        title: "Oops! Something went wrong",
        description: "We couldn't find the news alert logo file.",
      },
    },
    success: {
      type: "success" as const,
      title: "Success",
      description: "News alert logo uploaded successfully",
    },
  };

  const { logoFile } = useNewsAlertsStore.getState().createEditNewsAlert;

  if (!logoFile) {
    toast.error(toastMessages.error.noFile.description);
    resetCreateEditNewsAlertLogo();
    return false;
  }

  setIsUploadingNewsAlertLogo(true);

  try {
    const tenant = useAuth.getState().user.default_tenant?.slug;

    // Create FormData for multipart GraphQL request
    const formData = new FormData();
    formData.append(
      "operations",
      JSON.stringify({
        query: UploadNewsAlertLogoMutationString,
        variables: {
          input: {},
        },
      }),
    );

    formData.append("map", JSON.stringify({ "1": ["variables.input.logo"] }));

    if (!(logoFile instanceof File)) {
      toast.error("No valid file found");
      resetCreateEditNewsAlertLogo();
      return false;
    }
    const file = logoFile;

    // Use the filename without path to avoid absolute path issues
    const fileName = file.name.split(/[/\\]/).pop() || "logo";
    formData.append("1", file, fileName);

    const headers = await createHeaders("GET", "", tenant);

    const response = await fetch(`${dataServiceBaseUrl}/graphql/`, {
      method: "POST",
      headers,
      body: formData,
    });

    const result = await response.json();

    if (result.errors) {
      const errorMsg =
        result.errors[0]?.message || toastMessages.error.failed.description;
      toast.error(errorMsg);
      resetCreateEditNewsAlertLogo();
      return false;
    }

    const uploadResult = result.data?.uploadNewsAlertLogo;

    if (!uploadResult) {
      toast.error(toastMessages.error.failed.description);
      resetCreateEditNewsAlertLogo();
      return false;
    }

    if (uploadResult.__typename === "OperationInfo") {
      const errorMsg =
        uploadResult.messages?.[0]?.message ||
        toastMessages.error.failed.description;
      toast.error(errorMsg);
      resetCreateEditNewsAlertLogo();
      return false;
    }

    if (uploadResult.__typename !== "NewsAlertLogoType") {
      toast.error(toastMessages.error.failed.description);
      resetCreateEditNewsAlertLogo();
      return false;
    }

    const logo = uploadResult.logo;
    if (!logo || !logo.url) {
      toast.error(toastMessages.error.failed.description);
      resetCreateEditNewsAlertLogo();
      return false;
    }

    setNewsAlertLogoPreviewUrl(logo.url);
    setNewsAlertLogoId(uploadResult.id);

    setIsUploadingNewsAlertLogo(false);
    return true;
  } catch (error) {
    toast.error("An error occurred while uploading the news alert logo.");
    resetCreateEditNewsAlertLogo();
    return false;
  }
};

export {
  createNewsAlert,
  deleteNewsAlert,
  fetchNewsAlertById,
  generateNewsAlertPreview,
  updateNewsAlert,
  uploadNewsAlertLogo,
};
